import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { LayoutTemplate } from "lucide-react";
import { EmptyState } from "@/components/ui";
import { injectSelectHelper, isFrameMessage, type FrameToParent, type ParentToFrame } from "../selectMode";

export type Zoom = number | "fit";

interface Props {
  html: string;
  widthCm: number;
  heightCm: number;
  zoom: Zoom;
  selectMode: boolean;
  /** 当前选中元素路径; iframe 重新加载后据此恢复高亮 */
  selectedPath: number[] | null;
  onSelect: (sel: FrameToParent) => void;
}

/** 与生成 HTML 的 cm 单位一致: 1cm = 96/2.54 px */
const PX_PER_CM = 96 / 2.54;

/** 棋盘格画布背景 (透明区域一目了然, 像设计软件) */
export const CHECKERBOARD: CSSProperties = {
  backgroundColor: "hsl(var(--card))",
  backgroundImage:
    "linear-gradient(45deg, hsl(var(--muted)) 25%, transparent 25%, transparent 75%, hsl(var(--muted)) 75%)," +
    "linear-gradient(45deg, hsl(var(--muted)) 25%, transparent 25%, transparent 75%, hsl(var(--muted)) 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0, 8px 8px",
};

/**
 * iframe srcdoc 预览, 按真实像素尺寸渲染再 CSS 缩放。
 * sandbox 仅 allow-scripts (无 allow-same-origin): 注入的拖选脚本可运行,
 * 但 iframe 是不透明 origin, 无法访问父页 / cookie / localStorage。
 */
export function PreviewPane({ html, widthCm, heightCm, zoom, selectMode, selectedPath, onSelect }: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [fitScale, setFitScale] = useState(0.4);

  const vw = Math.max(1, Math.round((widthCm || 40) * PX_PER_CM));
  const vh = Math.max(1, Math.round((heightCm || 60) * PX_PER_CM));
  const scale = zoom === "fit" ? fitScale : zoom;

  const srcDoc = useMemo(() => (html ? injectSelectHelper(html) : ""), [html]);

  // 最新状态给 onLoad / message 回调使用
  const latest = useRef({ selectMode, selectedPath, onSelect });
  latest.current = { selectMode, selectedPath, onSelect };

  const post = (msg: ParentToFrame) => iframeRef.current?.contentWindow?.postMessage(msg, "*");

  // 适应容器
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const update = () => {
      const pad = 16;
      const s = Math.min((box.clientWidth - pad) / vw, (box.clientHeight - pad) / vh);
      setFitScale(Math.max(0.05, Math.min(1, s)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(box);
    return () => ro.disconnect();
  }, [vw, vh, html]);

  // 接收 iframe 消息
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow) return;
      if (isFrameMessage(e.data)) latest.current.onSelect(e.data);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // 状态变化同步到 iframe
  useEffect(() => { post({ type: "set-select-mode", on: selectMode }); }, [selectMode]);
  useEffect(() => { post({ type: "highlight-path", path: selectedPath }); }, [selectedPath]);

  const handleLoad = () => {
    post({ type: "set-select-mode", on: latest.current.selectMode });
    post({ type: "highlight-path", path: latest.current.selectedPath });
  };

  if (!html) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border border-dashed" style={CHECKERBOARD}>
        <EmptyState
          icon={LayoutTemplate}
          title="还没有模板"
          hint="在左侧填写尺寸和需求描述, 点「生成 HTML」后这里会显示预览"
          className="rounded-xl bg-card/90 px-6"
        />
      </div>
    );
  }

  return (
    <div ref={boxRef} className="h-full overflow-auto rounded-lg border p-2" style={CHECKERBOARD}>
      <div
        className="relative mx-auto shadow-md ring-1 ring-black/5"
        // 纸面固定白底 (模板 HTML 按白纸设计, 与主题无关)
        style={{ width: vw * scale, height: vh * scale, background: "#fff" }}
      >
        <iframe
          ref={iframeRef}
          srcDoc={srcDoc}
          sandbox="allow-scripts"
          title="模板预览"
          onLoad={handleLoad}
          className="absolute left-0 top-0 border-0"
          style={{ width: vw, height: vh, transform: `scale(${scale})`, transformOrigin: "top left" }}
        />
      </div>
    </div>
  );
}
